import React, { useMemo, useState } from "react";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { TIERS } from "../lib/brand";

/**
 * PuzzleCalculator
 *
 * Interactive widget where donors pick counts of each tier's puzzle piece.
 * Left: counters. Right: a live grid that fills with the selected pieces.
 * Bottom: running dollar total + submit callback.
 */

const PUZZLE_D =
  "M2,8 C2,4.7 4.7,2 8,2 L17,2 C17.5,2 18,1.4 18,0.8 C18,-2 22,-2 22,0.8 C22,1.4 22.5,2 23,2 L32,2 C35.3,2 38,4.7 38,8 L38,17 C38,17.5 38.6,18 39.2,18 C42,18 42,22 39.2,22 C38.6,22 38,22.5 38,23 L38,32 C38,35.3 35.3,38 32,38 L23,38 C22.5,38 22,38.6 22,39.2 C22,42 18,42 18,39.2 C18,38.6 17.5,38 17,38 L8,38 C4.7,38 2,35.3 2,32 L2,23 C2,22.5 1.4,22 0.8,22 C-2,22 -2,18 0.8,18 C1.4,18 2,17.5 2,17 Z";

export default function PuzzleCalculator({ onSelectionChange, onCheckout, onCardCheckout }) {
  const [counts, setCounts] = useState({ green: 0, yellow: 0, red: 0, blue: 1 });

  const orderedTiers = useMemo(
    () => ["green", "yellow", "red", "blue"].map((k) => TIERS.find((t) => t.key === k)),
    []
  );

  const total = useMemo(
    () => orderedTiers.reduce((s, t) => s + counts[t.key] * t.amount, 0),
    [counts, orderedTiers]
  );
  const totalCount = useMemo(
    () => Object.values(counts).reduce((a, b) => a + b, 0),
    [counts]
  );

  const set = (k, v) => {
    const next = { ...counts, [k]: Math.max(0, Math.min(99, v)) };
    setCounts(next);
    onSelectionChange && onSelectionChange({ counts: next, total: orderedTiers.reduce((s, t) => s + next[t.key] * t.amount, 0) });
  };

  const reset = () => {
    const next = { green: 0, yellow: 0, red: 0, blue: 0 };
    setCounts(next);
    onSelectionChange && onSelectionChange({ counts: next, total: 0 });
  };

  // Build a visual grid of the selected pieces (in a stable order per tier).
  const gridPieces = useMemo(() => {
    const arr = [];
    orderedTiers.forEach((t) => {
      for (let i = 0; i < counts[t.key]; i++) {
        arr.push({ color: t.color, tier: t.key });
      }
    });
    return arr;
  }, [counts, orderedTiers]);

  // Grid dimensions — grow smoothly with count.
  const cols = Math.min(8, Math.max(4, Math.ceil(Math.sqrt(Math.max(1, gridPieces.length)))));
  const rows = Math.ceil(Math.max(1, gridPieces.length) / cols);
  const CELL = 40;

  return (
    <div className="rounded-3xl bg-white border border-black/5 p-6 md:p-8" data-testid="puzzle-calculator">
      <div className="grid md:grid-cols-2 gap-8 items-start">
        {/* Counters */}
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Build your gift</div>
          <h3 className="mt-2 font-heading text-2xl font-semibold text-[#1F2937]">Pick your pieces.</h3>
          <p className="mt-2 text-sm text-[#6B7280]">
            Mix and match tiers. Every piece adds to the picture on the right.
          </p>

          <div className="mt-6 space-y-3">
            {orderedTiers.map((t) => (
              <div
                key={t.key}
                className="flex items-center justify-between rounded-2xl p-3 pl-4 border border-black/5"
                style={{ background: t.bgTint }}
                data-testid={`calc-row-${t.key}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${t.color}22`, border: `1px solid ${t.color}55` }}
                  >
                    <svg viewBox="0 0 40 40" className="w-5 h-5">
                      <path d={PUZZLE_D} fill={t.color} />
                    </svg>
                  </div>
                  <div className="leading-tight">
                    <div className="font-heading font-semibold text-[#1F2937] text-sm">
                      {t.name.split(" ")[0]} <span className="text-[#6B7280] font-normal">Piece</span>
                    </div>
                    <div className="text-xs text-[#6B7280]">${t.amount} each</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => set(t.key, counts[t.key] - 1)}
                    data-testid={`calc-dec-${t.key}`}
                    aria-label={`Decrease ${t.name}`}
                    className="w-8 h-8 rounded-full bg-white border border-black/10 flex items-center justify-center hover:bg-[#F4F1EA] active:scale-95 disabled:opacity-40"
                    disabled={counts[t.key] === 0}
                  >
                    <Minus className="w-4 h-4 text-[#1F2937]" />
                  </button>
                  <div
                    data-testid={`calc-count-${t.key}`}
                    className="min-w-[2.25rem] text-center font-heading font-bold text-[#1F2937] tabular-nums"
                  >
                    {counts[t.key]}
                  </div>
                  <button
                    type="button"
                    onClick={() => set(t.key, counts[t.key] + 1)}
                    data-testid={`calc-inc-${t.key}`}
                    aria-label={`Increase ${t.name}`}
                    className="w-8 h-8 rounded-full text-white flex items-center justify-center active:scale-95"
                    style={{ backgroundColor: t.color }}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 flex items-center justify-between">
            <button
              type="button"
              onClick={reset}
              data-testid="calc-reset-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#6B7280] hover:text-[#1F2937]"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
            <div className="text-xs text-[#6B7280]" data-testid="calc-pieces-count">
              {totalCount} piece{totalCount === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {/* Live grid preview */}
        <div>
          <div className="rounded-3xl bg-[#F4F1EA] border border-black/5 p-6 h-full min-h-[280px] flex flex-col">
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Live preview</div>
            <div className="flex-1 flex items-center justify-center py-4">
              {gridPieces.length === 0 ? (
                <div className="text-center text-sm text-[#6B7280] max-w-xs" data-testid="calc-empty">
                  Add a piece from the left to see your gift take shape.
                </div>
              ) : (
                <svg
                  viewBox={`0 0 ${cols * CELL} ${rows * CELL}`}
                  className="mx-auto"
                  style={{ width: "100%", maxWidth: cols * CELL, height: "auto" }}
                  data-testid="calc-grid"
                >
                  {gridPieces.map((p, i) => {
                    const c = i % cols;
                    const r = Math.floor(i / cols);
                    return (
                      <g
                        key={i}
                        transform={`translate(${c * CELL}, ${r * CELL})`}
                        style={{
                          filter: "drop-shadow(0 2px 2px rgba(31,41,55,0.15))",
                          opacity: 0,
                          animation: "calcPop 380ms ease-out both",
                          animationDelay: `${Math.min(i * 25, 500)}ms`,
                        }}
                      >
                        <path
                          d={PUZZLE_D}
                          fill={p.color}
                          stroke="#FDFBF7"
                          strokeWidth="1"
                        />
                      </g>
                    );
                  })}
                </svg>
              )}
            </div>
            <style>{`
              @keyframes calcPop {
                0% { opacity: 0; }
                100% { opacity: 1; }
              }
            `}</style>

            <div className="mt-4 flex items-end justify-between border-t border-black/5 pt-4 gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Your total</div>
                <div
                  className="mt-1 font-heading text-4xl font-bold text-[#1F2937] tabular-nums"
                  data-testid="calc-total"
                >
                  ${total.toLocaleString()}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {onCardCheckout && (
                  <button
                    type="button"
                    disabled={total === 0}
                    onClick={() => onCardCheckout({ counts, total })}
                    data-testid="calc-card-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-[#1F2937] hover:bg-black text-white px-5 py-3 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Pay with Card
                  </button>
                )}
                <button
                  type="button"
                  disabled={total === 0}
                  onClick={() => onCheckout && onCheckout({ counts, total })}
                  data-testid="calc-checkout-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#2196F3] hover:bg-[#1e88e5] text-white px-5 py-3 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Use Cash App
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
